import { Button } from "@progress/kendo-react-buttons";
import { SvgIcon } from "@progress/kendo-react-common";
import { Field, Form, FormElement } from "@progress/kendo-react-form";
import { Error, Label } from "@progress/kendo-react-labels";
import { Avatar, Card, CardBody, CardTitle } from "@progress/kendo-react-layout";
import { userIcon } from "@progress/kendo-svg-icons";
import { useEffect, useState } from "react";
import companiesApi from "../../api/company";
import usersApi from "../../api/users";
import { ICompany } from "../../components/admin/Company";
import { IUser } from "../../components/admin/User";
import { FormSwitchList, ListItem } from "../../components/form";
import { LoadingIndicator } from "../../components/LoadingIndicator";
import { useApi, useFetch } from "../../hooks/useApi";
import { useUserContext } from "../../hooks/useUserContext";
import { objectToLookup } from "../../utilities/objectUtilities";

export const Profile = () => {
  const { context } = useUserContext();
  const showPreferences = context.roles.isRiskTaker || context.roles.isBroker;

  const userResults = useFetch(usersApi.get);
  const saveProfile = useApi(usersApi.updateProfile);
  const companiesResults = useFetch(companiesApi.get);

  const [loading, setLoading] = useState(false);
  const [user, setUser] = useState<IUser | null>(null);
  const [companies, setCompanies] = useState<string[]>([]);
  const [preferencesList, setPreferencesList] = useState<ListItem[]>([]);

  useEffect(() => {
    if (userResults.data?.user) setUser(userResults.data.user);
    if (userResults.data?.preferences)
      setPreferencesList(objectToLookup(userResults.data.preferences, false));
  }, [userResults.data]);

  useEffect(() => {
    if (user && companiesResults.data?.companies) {
      setCompanies(
        companiesResults.data.companies
          .filter((c: ICompany) => user.companyIds.includes(c.id))
          .map((c: ICompany) => c.name)
          .sort((a: string, b: string) => a.localeCompare(b)),
      );
    }
  }, [user, companiesResults.data]);

  useEffect(() => {
    setLoading(userResults.loading || saveProfile.loading || companiesResults.loading);
  }, [userResults.loading, saveProfile.loading, companiesResults.loading]);

  interface IUserField {
    label: string;
    text?: string;
    list?: string[];
  }

  const UserField = ({ label, text, list }: IUserField) => {
    return (
      <div style={{ marginBottom: "1rem" }}>
        <Label>{label}</Label>
        {text && <div>{text}</div>}
        {list && list.map((item) => <div>{item}</div>)}
      </div>
    );
  };

  const handleSubmitEdit = async (event: any) => {
    const profile = { ...event };
    saveProfile.request(profile);
    setUser(profile);
  };

  return (
    <div className="profile-page" style={{ textAlign: "center" }}>
      <LoadingIndicator loading={loading} />
      {userResults.error && <Error>{userResults.error}</Error>}
      {saveProfile.error && <Error>{saveProfile.error}</Error>}
      {companiesResults.error && <Error>{companiesResults.error}</Error>}

      {!loading && user && (
        <Card style={{ border: "none", margin: "1rem" }}>
          <CardTitle>
            <Avatar type="icon" size="large">
              <SvgIcon icon={userIcon} />
            </Avatar>
            {user.fullName}
          </CardTitle>

          <CardBody>
            <UserField label={"Email Address"} text={user.email} />

            {user.phone && <UserField label={"Phone"} text={user.phone} />}

            {companies.length > 0 && <UserField label={"Companies"} list={companies} />}

            {showPreferences && (
              <>
                <Label>Preferences</Label>
                <div style={{ display: "flex", alignItems: "center" }}>
                  <Form
                    onSubmit={handleSubmitEdit}
                    initialValues={user}
                    render={(formRenderProps) => (
                      <FormElement>
                        <Field
                          name="preferences"
                          id="preferences"
                          component={FormSwitchList}
                          data={preferencesList}
                          disabled={loading}
                        />

                        <Button
                          themeColor="primary"
                          type="submit"
                          disabled={!formRenderProps.allowSubmit}
                        >
                          Save
                        </Button>
                      </FormElement>
                    )}
                  ></Form>
                </div>
              </>
            )}
          </CardBody>
        </Card>
      )}
    </div>
  );
};
